// package imports
import { Session } from 'next-auth';
import { Theme as MuiTheme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { KitButton } from 'components/common/cms-kit';

// local imports
import { MegaMenuSection } from '../SiteHeaderTypes';
import { useMegaMenuStore } from './UseMegaMenuStore';
import { AccountCircleIcon } from 'components/common/subcomponents/SvgIcons';
import { isCustomer } from 'services/subscriptionUtils';
import useMediaQuery from 'hooks/useMediaQuery';
import { isMyAccountSection } from '../SiteHeaderUtils';

const NavigationToggleButton = ({
  section,
  session,
}: {
  section: MegaMenuSection;
  session: Session | null;
}) => {
  const theme = useTheme() as MuiTheme;
  const { open, menuIsOpen, close, openMenu } = useMegaMenuStore();
  const isLargerScreen = useMediaQuery('(min-width: 1084px)');
  const isOpen =
    section.contentID != null &&
    openMenu === String(section.contentID) &&
    menuIsOpen;
  const isMyAccount = isMyAccountSection(section);
  const isLoggedIn = session?.user !== undefined;
  const isButton =
    typeof section.fields.displayAsButton === 'string' &&
    section.fields.displayAsButton === 'true';
  let displayButton = false;

  let content = <span className="flex-auto">{section.fields.menuTitle}</span>;
  if (isMyAccount) {
    if (isLoggedIn) {
      content = (
        <span className="flex-auto" id="loginLink">
          {`${session.user.firstName} ${typeof session.user.lastName === 'string' ? session.user.lastName.slice(0, 1) : ''}`}
        </span>
      );
    } else {
      content = (
        <span className="flex-auto" id="loginLink">
          <AccountCircleIcon
            className="w-5 h-5"
            color={theme.palette.primary.main}
          />
        </span>
      );
    }
  }

  if (isButton) {
    if (
      section.fields.freeAccountDisplayOnly === 'true' &&
      session?.user &&
      !isCustomer(session?.user)
    ) {
      displayButton = true;
    } else if (
      section.fields.notLoggedInUserOnlyDisplay === 'true' &&
      !isLoggedIn
    ) {
      displayButton = true;
    } else if (
      section.fields.loggedInUserDisplayOnly === 'true' &&
      isLoggedIn
    ) {
      displayButton = true;
    }
  }

  if (displayButton) {
    return (
      <div className="inline-flex items-center relative">
        <a
          href={section.fields?.links[0]?.fields?.link?.href}
          target={section.fields?.links[0]?.fields?.link?.target ?? '_self'}
          rel="noopener noreferrer"
        >
          <KitButton
            color={section.fields.buttonColor ?? 'primary'}
            size="sm"
            round
            style={{
              width: isLargerScreen ? '143px' : '111px',
              height: isLargerScreen ? '48px' : '38px',
              color: '#003b5c',
              fontSize: '.938rem',
              fontWeight: 700,
            }}
          >
            {section.fields.menuTitle}
          </KitButton>
        </a>
      </div>
    );
  }

  return (
    <button
      className={`inline-flex items-center text-sm xl:text-base font-serif relative whitespace-nowrap 
      before:absolute before:h-[2px] before:top-full before:transition-all before:duration-150 
      before:bg-light-blue text-blue font-semibold
      ${isMyAccount && !isLoggedIn ? '' : 'hover:before:w-full hover:before:opacity-100'} 
      `}
      onClick={() => {
        if (isOpen) {
          close();
        } else {
          open(String(section.contentID));
        }
      }}
      id={`nav-menu-button-${section.contentID}`}
      aria-haspopup="true"
      aria-expanded={isOpen}
      aria-controls={`nav-menu-${section.contentID}`}
      aria-label={`${isLoggedIn ? 'Logged in' : 'Not logged in'} - Toggle navigation menu`}
    >
      {content}
      <svg
        viewBox="0 0 10 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={`flex-none stroke-current w-2 ml-2 mr-1 transition ${isOpen ? 'rotate-180' : ''}`}
        role="img"
        aria-label="Navigation menu toggle"
      >
        <path
          className="stroke-[2px]"
          d="M1 1.5L4.29289 4.79289C4.68342 5.18342 5.31658 5.18342 5.70711 4.79289L9 1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
};

export default NavigationToggleButton;
